import { useEffect, useState } from "react"
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query"
import { tcmApi } from "./api"
import type { TcmActionRequest, TcmBridgeCreate, TcmBridgeUpdate, TcmCarePlan, TcmLang, TcmResourceBody } from "./types"

export const tcmKeys = {
  all: ["tcm"] as const,
  board: (orgId: string, scope: string) => ["tcm", "board", orgId, scope] as const,
  summary: ["tcm", "summary"] as const,
  episode: (id: string) => ["tcm", "episode", id] as const,
  note: (id: string) => ["tcm", "note", id] as const,
  carePlan: (id: string) => ["tcm", "care-plan", id] as const,
  resources: (orgId: string) => ["tcm", "resources", orgId] as const,
}

const MINUTE = 60 * 1000

export function useTcmBoard(orgId: string | null, scope: "active" | "closed") {
  return useQuery({
    queryKey: tcmKeys.board(orgId ?? "", scope),
    queryFn: () => tcmApi.getBoard(orgId as string, scope),
    enabled: !!orgId,
    staleTime: MINUTE,
    refetchInterval: 5 * MINUTE,
  })
}

/** Every listed clinic's board at once (Pear Admin's cross-clinic view). */
export function useTcmBoards(orgIds: string[], scope: "active" | "closed", enabled = true) {
  return useQueries({
    queries: orgIds.map((orgId) => ({
      queryKey: tcmKeys.board(orgId, scope),
      queryFn: () => tcmApi.getBoard(orgId, scope),
      enabled,
      staleTime: MINUTE,
      refetchInterval: 5 * MINUTE,
    })),
  })
}

export function useTcmSummary(enabled = true) {
  return useQuery({
    queryKey: tcmKeys.summary,
    queryFn: tcmApi.getSummary,
    enabled,
    staleTime: 2 * MINUTE,
    refetchInterval: 5 * MINUTE,
    retry: false,
  })
}

export function useTcmEpisode(episodeId: string | null) {
  return useQuery({
    queryKey: tcmKeys.episode(episodeId ?? ""),
    queryFn: () => tcmApi.getEpisode(episodeId as string),
    enabled: !!episodeId,
    staleTime: 0,
  })
}

export function useTcmNote(episodeId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: tcmKeys.note(episodeId ?? ""),
    queryFn: () => tcmApi.getNote(episodeId as string),
    enabled: !!episodeId && enabled,
    staleTime: 5 * MINUTE,
  })
}

export function useTcmAction(episodeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: TcmActionRequest) => tcmApi.act(episodeId, body),
    onSuccess: (detail) => {
      queryClient.setQueryData(tcmKeys.episode(episodeId), detail)
      queryClient.invalidateQueries({ queryKey: ["tcm", "board"] })
      queryClient.invalidateQueries({ queryKey: tcmKeys.summary })
      queryClient.invalidateQueries({ queryKey: tcmKeys.carePlan(episodeId) })
    },
  })
}

/** The case's latest care plan; `null` when there is none yet (the API's 404). */
export function useTcmCarePlan(episodeId: string | null) {
  return useQuery({
    queryKey: tcmKeys.carePlan(episodeId ?? ""),
    queryFn: async (): Promise<TcmCarePlan | null> => {
      try {
        return await tcmApi.getCarePlan(episodeId as string)
      } catch (err: any) {
        if (err?.response?.status === 404) return null
        throw err
      }
    },
    enabled: !!episodeId,
    staleTime: MINUTE,
    retry: false,
  })
}

/** The handout PDF as an object URL for an <iframe>; refetched when the plan changes. */
export function useHandoutPdf(episodeId: string, lang: TcmLang | null, preview: boolean, planStamp: string) {
  const query = useQuery({
    queryKey: ["tcm", "handout", episodeId, lang, preview, planStamp],
    queryFn: () => tcmApi.getHandoutPdf(episodeId, lang as TcmLang, preview),
    enabled: !!lang,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  })
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!query.data) return setUrl(null)
    const next = URL.createObjectURL(query.data)
    setUrl(next)
    return () => URL.revokeObjectURL(next)
  }, [query.data])
  return { ...query, url }
}

function useSetCarePlan(episodeId: string) {
  const queryClient = useQueryClient()
  return (plan: TcmCarePlan) => {
    queryClient.setQueryData(tcmKeys.carePlan(episodeId), plan)
    queryClient.invalidateQueries({ queryKey: ["tcm", "handout", episodeId] })
    queryClient.invalidateQueries({ queryKey: ["tcm", "board"] })
    queryClient.invalidateQueries({ queryKey: tcmKeys.episode(episodeId) })
  }
}

export function useUpdateBridge(episodeId: string) {
  const setPlan = useSetCarePlan(episodeId)
  return useMutation({
    mutationFn: ({ bridgeId, body }: { bridgeId: string; body: TcmBridgeUpdate }) =>
      tcmApi.updateBridge(episodeId, bridgeId, body),
    onSuccess: setPlan,
  })
}

export function useAddBridge(episodeId: string) {
  const setPlan = useSetCarePlan(episodeId)
  return useMutation({
    mutationFn: (body: TcmBridgeCreate) => tcmApi.addBridge(episodeId, body),
    onSuccess: setPlan,
  })
}

export function useTcmResources(orgId: string | null) {
  return useQuery({
    queryKey: tcmKeys.resources(orgId ?? ""),
    queryFn: () => tcmApi.getResources(orgId as string),
    enabled: !!orgId,
    staleTime: 5 * MINUTE,
  })
}

export function useSaveTcmResource(orgId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: TcmResourceBody }) =>
      id ? tcmApi.updateResource(orgId, id, body) : tcmApi.addResource(orgId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tcmKeys.resources(orgId) })
      queryClient.invalidateQueries({ queryKey: ["tcm", "care-plan"] })
      queryClient.invalidateQueries({ queryKey: ["tcm", "handout"] })
    },
  })
}
